import { useState } from 'react';
import * as XLSX from 'xlsx-js-style';
import toast from 'react-hot-toast';

// Botón que exporta el listado de máquinas a un archivo Excel (.xlsx)
// con formato: encabezado de color, filas alternadas y bordes.
//
// Usa `xlsx-js-style` (compatible con xlsx, pero con soporte de estilos).
//
// Props:
//   - maquinas: array con las máquinas (con sus relaciones cargadas)

// --- Paleta del documento ---
const AZUL_ENCABEZADO = '1E40AF'; // azul oscuro
const FILA_CLARA      = 'FFFFFF';
const FILA_ALTERNA    = 'EFF6FF'; // azul muy suave
const BORDE           = 'CBD5E1';

const bordeFino = {
  top:    { style: 'thin', color: { rgb: BORDE } },
  bottom: { style: 'thin', color: { rgb: BORDE } },
  left:   { style: 'thin', color: { rgb: BORDE } },
  right:  { style: 'thin', color: { rgb: BORDE } },
};

export default function ExportarMaquinasExcel({ maquinas = [] }) {
  const [exportando, setExportando] = useState(false);

  const exportar = () => {
    if (!maquinas.length) {
      toast.error('No hay máquinas para exportar');
      return;
    }

    setExportando(true);
    try {
      const encabezados = [
        'Cliente', 'Modelo Máquina', 'Serie', 'Departamento',
        'Modelo Tóner', 'Rendimiento Negro', 'Rendimiento Color', 'Tipo de Tóner', 'Color',
        'Contador Inicial', 'Guía de Entrega', 'Fecha Instalación',
      ];

      const filas = maquinas.map((m) => [
        m.clientes?.nombre || '',
        m.modelo || '',
        m.serie || '',
        m.departamento || '',
        m.toners?.modelo || '',
        m.toners?.rendimiento ?? '',
        // Solo aplica a máquinas de color; si el tóner no tiene rendimiento
        // de color definido, sus colores usan el mismo del negro.
        m.es_color ? (m.toners?.rendimiento_color ?? m.toners?.rendimiento ?? '') : '',
        m.tipos_toner?.nombre || '',
        m.es_color ? 'Sí' : 'No',
        m.contador_inicial ?? '',
        m.guia_entrega || '',
        m.fecha_instalacion
          ? new Date(m.fecha_instalacion).toLocaleDateString('es-CL')
          : '',
      ]);

      const hoja = XLSX.utils.aoa_to_sheet([encabezados, ...filas]);

      // ---- Estilo del encabezado ----
      encabezados.forEach((_, col) => {
        const ref = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!hoja[ref]) return;
        hoja[ref].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          fill: { patternType: 'solid', fgColor: { rgb: AZUL_ENCABEZADO } },
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
          border: bordeFino,
        };
      });

      // ---- Estilo de las filas (alternadas) ----
      filas.forEach((fila, i) => {
        const colorFondo = i % 2 === 0 ? FILA_CLARA : FILA_ALTERNA;
        fila.forEach((_, col) => {
          const ref = XLSX.utils.encode_cell({ r: i + 1, c: col });
          if (!hoja[ref]) return;
          // Columnas numéricas o de estado van centradas
          const centrada = [5, 6, 8, 9].includes(col);
          hoja[ref].s = {
            font: { sz: 10 },
            fill: { patternType: 'solid', fgColor: { rgb: colorFondo } },
            alignment: {
              horizontal: centrada ? 'center' : 'left',
              vertical: 'center',
            },
            border: bordeFino,
          };
        });
      });

      // ---- Ancho de columnas ----
      hoja['!cols'] = [
        { wch: 38 }, { wch: 20 }, { wch: 14 }, { wch: 24 },
        { wch: 16 }, { wch: 13 }, { wch: 13 }, { wch: 14 }, { wch: 8 },
        { wch: 16 }, { wch: 16 }, { wch: 17 },
      ];

      // ---- Alto de la fila de encabezado ----
      hoja['!rows'] = [{ hpt: 26 }];

      // ---- Congelar la primera fila (queda fija al desplazarse) ----
      hoja['!freeze'] = { xSplit: 0, ySplit: 1 };
      hoja['!autofilter'] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: filas.length, c: encabezados.length - 1 },
        }),
      };

      const libro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(libro, hoja, 'Máquinas');

      const hoy = new Date().toISOString().split('T')[0];
      XLSX.writeFile(libro, `Maquinas_${hoy}.xlsx`);

      toast.success(`${filas.length} máquinas exportadas`);
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      toast.error('No se pudo generar el archivo. Intenta nuevamente.');
    } finally {
      setExportando(false);
    }
  };

  return (
    <button
      onClick={exportar}
      disabled={exportando || !maquinas.length}
      className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
      title="Descargar el listado completo de máquinas en Excel"
    >
      {exportando ? (
        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )}
      {exportando ? 'Generando...' : 'Exportar a Excel'}
    </button>
  );
}
